import {
  Button,
  HSpacer,
  InfoTooltip,
  Text,
  TextLink,
  Toolbar,
  VSpacer,
} from '@/components/DesignSystem';
import {
  Filter,
  FilterOption,
  FilterSelections,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { ChurnStatusChip } from '@/components/shared/ChurnStatusChip';
import { TruncatedHeaderText } from '@/components/shared/TruncatedHeaderText';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { CustomerChurn } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useGetRetailerLocations } from '@/hooks/useRetailerLocations';
import { useSearch } from '@/hooks/useSearch';
import { LinkedAccountsDialog } from '@/pages/FarmerList/LinkedAccountsDialog';
import { LocationsDialog } from '@/pages/RetailerOverview/PointsOverview/LocationsDialog';
import { SalespeopleDialog } from '@/pages/RetailerOverview/PointsOverview/SalespeopleDialog';
import {
  PurchaseCategoryFilterChip,
} from '@/pages/RetailerOverview/PointsOverview/PurchaseCategoryFilterChip';
import {
  ProgramPeriodFilterChip,
} from '@/pages/RetailerOverview/ProgramPeriodFilterChip';
import {
  ViewCustomerDetailsFullScreenModal,
} from '@/pages/RetailerOverview/ViewCustomerDetailsFullScreenModal';
import { ExportReportDialog } from '@/pages/Reports/ExportReportDialog';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { RetailerEndpoint } from '@api/endpoints';
import { ApiRetailer, ApiRetailerLocation, ApiUser } from '@api/interfaces';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import Link from '@mui/icons-material/Link';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import { ChurnStatus, ReportType, UserType } from '@shared/enums';
import { SharedConfig } from '@shared/SharedConfig';
import { getAccountName, getCategoriesToNextTier, getTierName } from '@shared/utilities';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from 'react-query';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface TierProgressCustomersReportProps {
  onCloseExportDialog: () => void,
  onProgramChange?: (program: ApiRewardsProgram) => void,
  program: ApiRewardsProgram,
  retailer: ApiRetailer,
  showExportConfirmationDialog: boolean,
}

export const TierProgressCustomersReport = ({
  onCloseExportDialog,
  onProgramChange,
  program,
  retailer,
  showExportConfirmationDialog,
}: TierProgressCustomersReportProps) => {
  const { t } = useTranslation();
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => new Map());
  const [purchaseCategoryIds, setPurchaseCategoryIds] = useState<string[]>([]);
  const [hasPurchasedCategory, setHasPurchasedCategory] = useState<boolean | undefined>(
    undefined,
  );
  const [sortBy, setSortBy] = (
    useState<RetailerEndpoint.ListTierCustomers.TierCustomersSort>('rankCurrent')
  );
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedLinkedAccountsCustomer, setSelectedLinkedAccountsCustomer] = useState<ApiUser>();
  const [selectedViewAccountCustomer, setSelectedViewAccountCustomer] = useState<ApiUser>();
  const [customerLocationsToDisplay, setCustomerLocationsToDisplay] = (
    useState<{ customer: ApiUser, locations: ApiRetailerLocation[] }>()
  );
  const [customerSalespeopleToDisplay, setCustomerSalespeopleToDisplay] = (
    useState<{ customer: ApiUser, salespeople: ApiUser[] }>()
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!tableRef.current) return;
      const { top } = tableRef.current.getBoundingClientRect();
      setTableHeight(window.innerHeight - top - 64);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (tableRef.current?.parentElement) {
      observer.observe(tableRef.current.parentElement);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const {
    search: customerSearch,
    debouncedSearch: debouncedCustomerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();
  const { retailerLocations } = useGetRetailerLocations(
    retailer.id,
    { limit: SharedConfig.maxPageLimit },
  );
  const theme = useTheme();
  const { isRetailerAdmin } = useAuthentication();
  const showChurnData = isRetailerAdmin && retailer.isChurnEnabled;

  const locationFilterOptions: FilterOption[] = retailerLocations?.data.map((location) => ({
    id: location.id,
    label: location.name,
  })) ?? [];

  const stringifiedFilterSelections = JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  const _getCategoriesToNextTierText = (customer: ApiUser) => {
    const farmerReward = getProgramReward(customer.farmerRewards);
    return t(
      $ => $.customers.progress.categoryCount,
      { count: getCategoriesToNextTier(farmerReward, program?.tiers ?? []) },
    );
  };

  const getProgramReward = (farmerRewards: ApiFarmerRewards[] | undefined) => {
    return farmerRewards?.find((reward) => reward.programId === program?.id);
  };

  const { churnStatus } = CustomerChurn.columns;

  const handleSort = (newSortBy: RetailerEndpoint.ListTierCustomers.TierCustomersSort) => {
    const isSortCurrentlySelected = newSortBy === sortBy;
    if (isSortCurrentlySelected) {
      if (newSortBy === 'rankCurrent') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy('rankCurrent');
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(newSortBy);
      setSortDesc(true);
    }
  };

  const { data: filterOptions } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW_FILTER_OPTIONS, retailer.id, program.id],
    () => RetailerApi.getPointsOverviewFilterOptions(retailer.id, { programId: program.id }),
  );

  const {
    data: primaryCustomers,
    fetchNextPage: fetchNextPrimaryCustomersPage,
    hasNextPage: hasNextPrimaryCustomersPage,
    isFetching: isFetchingNextPrimaryCustomersPage,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, retailer.id, debouncedCustomerSearch],
    async ({ pageParam = 0 }) => (
      UserApi.list(
        {
          limit: 20,
          page: pageParam,
          retailerId: retailer.id,
          search: debouncedCustomerSearch,
          userType: [UserType.Farmer],
        },
      )
    ),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const queryBody: RetailerEndpoint.ListTierCustomers.Query = useMemo(() => {
    const selectedCustomers = Array.from(filterSelections?.get('customer') ?? []);
    const selectedCurrentStatuses = Array.from(filterSelections?.get('current-status') ?? []);
    const selectedReEarnedTiers = Array.from(filterSelections?.get('reearned-tier') ?? []);
    const selectedSalespersons = Array.from(filterSelections?.get('salesperson') ?? []);
    const selectedLocations = Array.from(filterSelections?.get('location') ?? []);

    return {
      ...(selectedCustomers.length ? { id: selectedCustomers } : {}),
      ...(selectedCurrentStatuses.length
        ? { rankCurrent: selectedCurrentStatuses.map((tier) => Number(tier)) } : {}),
      ...(selectedReEarnedTiers.length
        ? { rankEarned: selectedReEarnedTiers.map((tier) => Number(tier)) } : {}),
      ...(selectedSalespersons.length ? { salespersonId: selectedSalespersons } : {}),
      ...(selectedLocations.length ? { locationId: selectedLocations } : {}),
      ...(program ? { programId: program.id } : {}),
      ...(hasPurchasedCategory === true ? { hasPurchasedCategoryId: purchaseCategoryIds } : {}),
      ...(hasPurchasedCategory === false ? { hasNotPurchasedCategoryId: purchaseCategoryIds } : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedFilterSelections, purchaseCategoryIds, hasPurchasedCategory, program.id]);

  const {
    data: customers,
    fetchNextPage: fetchNextTierCustomersPage,
    hasNextPage: hasNextTierCustomersPage,
    isFetchingNextPage: isFetchingNextTierCustomersPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.LIST_TIER_CUSTOMERS, retailer.id, queryBody, sortBy, sortDesc],
    async ({ pageParam }) => (
      RetailerApi.listTierCustomers(
        retailer.id,
        {
          limit: 20,
          page: pageParam,
          sort: sortBy,
          sortDesc: sortDesc,
          ...queryBody,
        },
      )
    ),
    {
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page
          ? lastPage.page + 1
          : undefined
      ),
    },
  );

  const tierOptions = program?.tiers?.map(
    (tier) => ({ id: tier.rank.toString(), label: tier.name }),
  ) ?? [];
  const filters: Filter[] = useMemo(() => {
    const filters: Filter[] = [
      {
        id: 'customer',
        infiniteScrollOptions: {
          fetchMoreItems: fetchNextPrimaryCustomersPage,
          hasMore: hasNextPrimaryCustomersPage ?? true,
          isLoading: isFetchingNextPrimaryCustomersPage,
        },
        isChipLabelVerbose: true,
        label: t($ => $.common.customer),
        onSearch: (search) => setCustomerSearch(search),
        options: (primaryCustomers?.pages?.flatMap(({ data }) => data) ?? []).map((customer) => ({
          id: customer.id,
          label: getAccountName(customer),
        })) ?? [],
        search: customerSearch,
        selectionMethod: 'multi-select',
      },
      {
        id: 'current-status',
        isChipLabelVerbose: true,
        label: t($ => $.overview.tierProgress.currentStatus),
        options: [{ id: '-1', label: t($ => $.common.status.none) }, ...tierOptions],
        selectionMethod: 'multi-select',
      },
      {
        id: 'reearned-tier',
        isChipLabelVerbose: true,
        label: t($ => $.overview.tierProgress.reEarnedTier),
        options: [{ id: '-1', label: t($ => $.common.status.none) }, ...tierOptions],
        selectionMethod: 'multi-select',
      },
      {
        id: 'salesperson',
        isChipLabelVerbose: true,
        label: t($ => $.overview.tierProgress.salesperson),
        options: filterOptions?.salespersons ?? [],
        selectionMethod: 'multi-select',
      },
      {
        id: 'location',
        isChipLabelVerbose: true,
        label: t($ => $.overview.tierProgress.location),
        options: locationFilterOptions,
        selectionMethod: 'multi-select',
      },
    ];

    return filters;
  }, [
    t,
    customerSearch,
    fetchNextPrimaryCustomersPage,
    filterOptions,
    hasNextPrimaryCustomersPage,
    isFetchingNextPrimaryCustomersPage,
    locationFilterOptions,
    primaryCustomers,
    program,
    retailer,
    setCustomerSearch,
    tierOptions,
  ]);

  const hasPurchaseCategorySelections = Array.from(filterSelections.values())
    .some((s) => s.size > 0);

  const renderTableData = () => {
    if (isLoading) {
      return null;
    }
    return (
      <>
        {(customers?.pages?.flatMap(({ data }) => data) ?? []).map((customer) => {
          const linkedAccounts = customer.accountGroupUsers ?? [];
          const { locations, salespersons } = customer;
          return (
            <TableRow key={customer.id}>
              <TableCell height={67} sx={{ overflowWrap: 'anywhere' }} width={255}>
                {getAccountName(customer)}
                <Text category="body-xsmall" sx={{ color: theme.palette.text.secondary }}>
                  {t(($) => $.common.labels.idHash, { id: customer.externalId })}
                </Text>
              </TableCell>
              <TableCell height={67} width={171}>
                {getTierName(
                  program?.tiers ?? [],
                  getProgramReward(customer.farmerRewards)?.rankCurrent,
                  t($ => $.common.status.none),
                )}
              </TableCell>
              <TableCell height={67} width={176}>
                {getTierName(
                  program?.tiers ?? [],
                  getProgramReward(customer.farmerRewards)?.rankEarned,
                  t($ => $.common.status.none),
                )}
              </TableCell>
              <TableCell height={67} width={152}>
                {_getCategoriesToNextTierText(customer)}
              </TableCell>
              {showChurnData && (
                <TableCell height={67} width={152}>
                  <ChurnStatusChip status={customer?.churnStatus as ChurnStatus} />
                </TableCell>
              )}
              <TableCell height={67} width={130}>
                {!salespersons?.length
                  ? t($ => $.common.none)
                  : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setCustomerSalespeopleToDisplay({ customer, salespeople: salespersons });
                      }}
                      testID={`${customer.id}-salespeople-button`}
                    >
                      {t($ => $.common.units.salespeopleCount, { count: salespersons.length })}
                    </TextLink>
                  )
                }
              </TableCell>
              <TableCell height={67} width={115}>
                {!locations?.length
                  ? t($ => $.common.none)
                  : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setCustomerLocationsToDisplay({ customer, locations });
                      }}
                      testID={`${customer.id}-locations-button`}
                    >
                      {t($ => $.common.units.locationsCount, { count: locations.length })}
                    </TextLink>
                  )
                }
              </TableCell>
              <TableCell height={67} width={138}>
                {!linkedAccounts.length
                  ? t($ => $.common.status.none)
                  : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedLinkedAccountsCustomer(customer);
                      }}
                      testID={`${customer.id}-linked-accounts-button`}
                    >
                      <Link sx={{ marginRight: '6px', transform: 'rotate(315deg)' }} />
                      {linkedAccounts.length}{' '}
                      {linkedAccounts.length === 1
                        ? t($ => $.common.filters.account)
                        : t($ => $.common.filters.accounts)}
                    </TextLink>
                  )
                }
              </TableCell>
              <TableCell height={67} width={132}>
                <Button
                  onClick={() => setSelectedViewAccountCustomer(customer)}
                  size="small"
                  square
                  testID={`${customer.id}-view-account-button`}
                >
                  {t($ => $.common.actions.viewAccount)}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  return (
    <Stack>
      <Stack alignItems="center" direction="row" flexWrap="wrap">
        <Toolbar
          filterSelections={filterSelections}
          filters={filters}
          hideSearch
          onChange={({ selections }) => {
            setFilterSelections(selections ?? new Map());
          }}
          retainSelectionsOnFilterChange
          testID="tier-progress-cutomers-filters"
        />
        <PurchaseCategoryFilterChip
          hasPurchasedCategory={hasPurchasedCategory}
          onChange={(categoryIds, hasPurchased) => {
            setPurchaseCategoryIds(categoryIds);
            setHasPurchasedCategory(hasPurchased);
          }}
          options={filterOptions?.purchaseCategories ?? []}
          purchaseCategoryIds={purchaseCategoryIds}
          testID="tier-progress-purchase-category-filter-chip"
        />
        {!!onProgramChange && (
          <>
            <HSpacer size="3" />
            <ProgramPeriodFilterChip
              onSelectProgram={onProgramChange}
              programs={agronomyProgramsOnly(retailer.rewardsPrograms)}
              selectedProgram={program}
              testID="tier-progress-program-period-filter-chip"
            />
          </>
        )}
        {(hasPurchaseCategorySelections || purchaseCategoryIds.length > 0) && (
          <>
            <HSpacer size="3" />
            <TextLink
              category="title-medium"
              onClick={() => {
                setFilterSelections(new Map());
                setHasPurchasedCategory(undefined);
                setPurchaseCategoryIds([]);
              }}
              testID="tier-progress-filters-clear-all-button"
            >
              {t(($) => $.common.actions.clearAll)}
            </TextLink>
          </>
        )}
      </Stack>
      <VSpacer size="3" />
      <Stack direction="row" justifyContent="space-between">
        <Text category="body-small" textAlign="left">
          {t($ => $.common.units.itemsCount, { count: customers?.pages?.[0]?.total ?? 0 })}
        </Text>
        <Text category="body-small" textAlign="right">
          {t($ => $.overview.selectedProgramPeriodOnly)}
        </Text>
      </Stack>
      <VSpacer size="3" />
      <div ref={tableRef} />
      <InfiniteScroll
        fetchMoreItems={fetchNextTierCustomersPage}
        hasMore={hasNextTierCustomersPage ?? true}
        height={tableHeight > 0 ? `${tableHeight}px` : undefined}
        isLoading={isLoading || isFetchingNextTierCustomersPage}
      >
        <Table sx={{ '& .MuiTableCell-body': { py: '4px' }, tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              '& .MuiTableCell-head': { py: '4px' },
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell height={76} variant="head" width={255}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('name')}
                  sx={{ '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } }}
                >
                  <TruncatedHeaderText>{t($ => $.common.customer)}</TruncatedHeaderText>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={171}>
                <TableSortLabel
                  active={sortBy === 'rankCurrent' && !sortDesc}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('rankCurrent')}
                  sx={{ '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } }}
                >
                  <TruncatedHeaderText>
                    {t($ => $.overview.tierProgress.currentStatusWithAsterisk)}
                  </TruncatedHeaderText>
                  <HSpacer size="2" />
                  <InfoTooltip color="info">
                    {t($ => $.overview.kpis.currentStatusTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={176}>
                <TableSortLabel
                  active={sortBy === 'rankEarned'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('rankEarned')}
                  sx={{ '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } }}
                >
                  <TruncatedHeaderText>
                    {t($ => $.overview.tierProgress.reEarnedTierWithAsterisk)}
                  </TruncatedHeaderText>
                  <HSpacer size="2" />
                  <InfoTooltip color="info">
                    {t($ => $.overview.tierProgress.reEarnedTierTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={152}>
                <TableSortLabel
                  active={sortBy === 'segmentsRemaining'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('segmentsRemaining')}
                  sx={{ '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } }}
                >
                  <TruncatedHeaderText>
                    {t($ => $.overview.tierProgress.categoriesToReachNextTierWithAsterisk)}
                  </TruncatedHeaderText>
                  <InfoTooltip color="info">
                    {t($ => $.overview.tierProgress.categoriesToReachNextTierTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              {showChurnData && (
                <>
                  <TableCell height={76} variant="head" width={200}>
                    <TableSortLabel
                      active={sortBy === churnStatus?.key}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('churnStatus')}
                      sx={{ '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } }}
                    >
                      <TruncatedHeaderText>
                        {churnStatus?.label} {t(($) => $.insights.churn.beta)}
                      </TruncatedHeaderText>
                      <HSpacer size="2" />
                      <InfoTooltip color="info">
                        {churnStatus?.description}
                      </InfoTooltip>
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.salesperson)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={115}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.location)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={138}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.linkedAccounts)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={132} />
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableData()}
          </TableBody>
        </Table>
      </InfiniteScroll>
      {showExportConfirmationDialog && (
        <ExportReportDialog
          confirmMessage={t($ => $.overview.tierProgress.exportWarning)}
          onClose={onCloseExportDialog}
          request={{
            params: { sort: sortBy, sortDesc, ...queryBody },
            type: ReportType.TierProgress,
          }}
          retailerId={retailer.id}
        />
      )}
      {!!selectedLinkedAccountsCustomer && (
        <LinkedAccountsDialog
          customer={selectedLinkedAccountsCustomer}
          onClose={() => setSelectedLinkedAccountsCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
          show
        />
      )}
      {!!customerLocationsToDisplay && (
        <LocationsDialog
          customer={customerLocationsToDisplay.customer}
          locations={customerLocationsToDisplay.locations}
          onClose={() => setCustomerLocationsToDisplay(undefined)}
        />
      )}
      {!!customerSalespeopleToDisplay && (
        <SalespeopleDialog
          customer={customerSalespeopleToDisplay.customer}
          onClose={() => setCustomerSalespeopleToDisplay(undefined)}
          salespeople={customerSalespeopleToDisplay.salespeople}
        />
      )}
      {!!selectedViewAccountCustomer && (
        <ViewCustomerDetailsFullScreenModal
          id={selectedViewAccountCustomer.id}
          onClose={() => setSelectedViewAccountCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
        />
      )}
    </Stack>
  );
};
